import React, { PureComponent } from 'react';
import {
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Checkbox,
  Grid,
  Typography,
  TablePagination,
  TableFooter,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/Delete';
import { InputAdornment } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { Tooltip } from '@mui/material';
import { Avatar } from '@mui/material';
import { arrayOf, bool, func, object, string } from 'prop-types';
import { injectIntl, intlShape } from 'react-intl';
import { isEqual } from 'lodash';
import { PAGINATION_PER_PAGE } from 'constants/Values';
import { SimpleText } from 'util/IntlMessages';
import { Button } from '../Button';
import dialogStyles from './multi-select-dialog.module.scss';
import styles from './styles.module.scss';
import tableStyles from '../DefinableTable/styles.module.scss';
import studentPlaceholder from '../../assets/images/student-placeholder.png';

export const OPTION_TYPE = {
  RESULT: 'RESULT',
  SELECTED: 'SELECTED',
  EXCLUDED: 'EXCLUDED', // not implemented
};

export const INTENTION_TYPE = {
  ADD: 'ADD',
  MODIFY: 'MODIFY',
};

class MultiSelectDialog extends PureComponent {
  static propTypes = {
    intention: string,
    intl: intlShape,
    isLoading: bool,
    onSearch: func,
    onChange: func,
    onSubmit: func,
    onClose: func,
    selectedOptions: arrayOf(object),
    options: arrayOf(object),
    optionRenderer: func,
    titleLabelId: string.isRequired,
    noResultsLabelId: string,
    searchingLabelId: string,
    placeholderLabelId: string,
    submitActionLabelId: string,
    noSelectionLabelId: string,
    removeActionTooltipLabelId: string,
    selectedHeadingLabelId: string,
  };

  static defaultProps = {
    intention: INTENTION_TYPE.ADD,
    noResultsLabelId: 'multi-select.search.no-results',
    searchingLabelId: 'multi-select.search.searching',
    placeholderLabelId: 'multi-select.search.placeholder',
    noSelectionLabelId: 'multi-select.no-selections',
    selectedHeadingLabelId: 'multi-select.heading.selected',
    removeActionTooltipLabelId: 'multi-select.action.remove.tooltip',
    submitActionLabelId: 'multi-select.button.submit',
    optionRenderer: MultiSelectDialog.prototype.renderOption,
  };

  constructor(props) {
    super(props);
    const selectedOptionIds = new Set(
      props.selectedOptions && props.selectedOptions.map(option => option.id),
    );
    this.state = {
      searchText: '',
      selectedOptionIds,
      initialItemIds: props.intention === INTENTION_TYPE.MODIFY ? selectedOptionIds : undefined,
      optionsPagination: {
        page: 0,
        rowsPerPage: PAGINATION_PER_PAGE[0],
      },
      selectedOptionsPagination: {
        page: 0,
        rowsPerPage: PAGINATION_PER_PAGE[0],
      },
    };
  }

  componentDidMount() {
    const { searchText } = this.state;
    this.props.onSearch(searchText);
  }

  handleSearchChange = event => {
    const searchText = event.target.value;
    this.setState({ searchText });
    this.props.onSearch(searchText);
  };

  handleToggleOption = option => {
    const { selectedOptionIds } = this.state;
    const newSelectedOptionIds = new Set(selectedOptionIds);

    if (newSelectedOptionIds.has(option.id)) {
      newSelectedOptionIds.delete(option.id);
      this.setState({ selectedOptionIds: newSelectedOptionIds }, () => {
        this.props.onChange(this.props.selectedOptions.filter(opt => option.id !== opt.id));
      });
    } else {
      newSelectedOptionIds.add(option.id);
      this.setState({ selectedOptionIds: newSelectedOptionIds }, () => {
        this.props.onChange([...this.props.selectedOptions, option]);
      });
    }
  };

  // eslint-disable-next-line no-unused-vars
  renderOption(option, optionType) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center' }} className="pointer">
        <Avatar
          alt={option.fullName}
          src={option.pathToAvatar || studentPlaceholder}
          sx={{ mr: 1 }}
          style={{ width: 32, height: 32 }}
        />
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          <span>{option.fullName}</span>
          <span style={{ color: '#999999' }}>
            {this.props.intl.formatMessage(
              { id: 'dialog.add.stop.search.schoolId' },
              { schoolId: option.schoolId },
            )}
          </span>
        </div>
      </Box>
    );
  }

  renderOptionsPagination = () => {
    const { intl, options } = this.props;
    return (
      <TablePagination
        count={(options && options.length) || 0}
        // MUI 7 names — see DefinableTable's pagination for what the v4 ones did.
        onPageChange={(e, page) => {
          this.setState(prevState => ({
            optionsPagination: { ...prevState.optionsPagination, page },
          }));
        }}
        onRowsPerPageChange={e => {
          this.setState(prevState => ({
            optionsPagination: {
              ...prevState.optionsPagination,
              rowsPerPage: e.target.value,
              page: 0,
            },
          }));
        }}
        page={this.state.optionsPagination.page || 0}
        rowsPerPage={this.state.optionsPagination.rowsPerPage || PAGINATION_PER_PAGE[0]}
        rowsPerPageOptions={PAGINATION_PER_PAGE}
        labelRowsPerPage={intl.formatMessage({
          id: 'component.table.pagination.labelRowsPerPage',
        })}
      />
    );
  };

  renderSelectedOptionsPagination = () => {
    const { intl, selectedOptions } = this.props;
    const { selectedOptionsPagination } = this.state;
    return (
      <TablePagination
        count={(selectedOptions && selectedOptions.length) || 0}
        onPageChange={(e, page) => {
          this.setState(prevState => ({
            selectedOptionsPagination: { ...prevState.selectedOptionsPagination, page },
          }));
        }}
        onRowsPerPageChange={e => {
          this.setState(prevState => ({
            selectedOptionsPagination: {
              ...prevState.selectedOptionsPagination,
              rowsPerPage: e.target.value,
              page: 0,
            },
          }));
        }}
        page={selectedOptionsPagination.page || 0}
        rowsPerPage={selectedOptionsPagination.rowsPerPage || PAGINATION_PER_PAGE[0]}
        rowsPerPageOptions={PAGINATION_PER_PAGE}
        labelRowsPerPage={intl.formatMessage({
          id: 'component.table.pagination.labelRowsPerPage',
        })}
      />
    );
  };

  render() {
    const {
      searchText,
      selectedOptionIds,
      initialItemIds,
      optionsPagination,
      selectedOptionsPagination,
    } = this.state;
    const {
      onClose,
      onSubmit,
      options,
      selectedOptions,
      intl,
      isLoading,
      titleLabelId,
      placeholderLabelId,
      searchingLabelId,
      noResultsLabelId,
      submitActionLabelId,
      noSelectionLabelId,
      removeActionTooltipLabelId,
      selectedHeadingLabelId,
      optionRenderer,
      intention,
    } = this.props;

    let paginatedOptions = options;
    if (options && options.length > PAGINATION_PER_PAGE[0] * 2) {
      const { page, rowsPerPage } = optionsPagination;
      const startIndex = page * rowsPerPage;
      const endIndex = startIndex + rowsPerPage;
      paginatedOptions = options.slice(startIndex, endIndex);
    }
    let paginatedSelectedOptions = selectedOptions;
    if (selectedOptions && selectedOptions.length > PAGINATION_PER_PAGE[0] * 2) {
      const { page, rowsPerPage } = selectedOptionsPagination;
      const startIndex = page * rowsPerPage;
      const endIndex = startIndex + rowsPerPage;
      paginatedSelectedOptions = selectedOptions.slice(startIndex, endIndex);
    }

    return (
      <Dialog
        open
        onClose={onClose}
        className="notice-template-dialog"
        aria-labelledby="form-dialog-title"
        maxWidth={false}
      >
        <DialogTitle id="form-dialog-title" className="dialog-title" style={{ minWidth: '50vw' }}>
          <SimpleText id={titleLabelId} />
          <IconButton color="inherit" onClick={onClose} aria-label="Close">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={3} style={{ height: '60vh' }}>
            <Grid style={{ borderRight: '1px solid #eeeeee', height: '100%' }} size={6}>
              <div className={tableStyles.root}>
                <Table padding="dense">
                  <TableHead className={dialogStyles.tableHead}>
                    <TableRow>
                      <TableCell colSpan={2} padding="none">
                        <TextField
                          sx={{ px: 2 }}
                          fullWidth
                          placeholder={intl.formatMessage({
                            id: placeholderLabelId,
                          })}
                          value={searchText}
                          onChange={this.handleSearchChange}
                          slotProps={{
                            input: {
                              startAdornment: (
                                <InputAdornment position="start">
                                  <SearchIcon />
                                </InputAdornment>
                              ),
                            },
                          }}
                        />
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody className={tableStyles.tableBody}>
                    {(!paginatedOptions || paginatedOptions.length === 0) &&
                      searchText.length > 0 && (
                        <TableRow>
                          <TableCell padding="none" align="center">
                            <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                              <SimpleText id={isLoading ? searchingLabelId : noResultsLabelId} />
                            </Box>
                          </TableCell>
                        </TableRow>
                      )}
                    {paginatedOptions &&
                      paginatedOptions.length > 0 &&
                      paginatedOptions.map(option => (
                        <TableRow key={option.id}>
                          <TableCell padding="none" style={{ width: 48 }}>
                            <Checkbox
                              color="primary"
                              checked={selectedOptionIds.has(option.id)}
                              onChange={() => this.handleToggleOption(option)}
                            />
                          </TableCell>
                          <TableCell padding="none" onClick={() => this.handleToggleOption(option)}>
                            {optionRenderer.apply(this, [option, OPTION_TYPE.RESULT])}
                          </TableCell>
                        </TableRow>
                      ))}
                  </TableBody>
                  {options &&
                    options.length > PAGINATION_PER_PAGE[0] * 2 && (
                      <TableFooter>
                        <TableRow>{this.renderOptionsPagination()}</TableRow>
                      </TableFooter>
                    )}
                </Table>
              </div>
            </Grid>
            <Grid sx={{ height: '100%' }} size={6}>
              <Box className={tableStyles.root} sx={{ height: '100%' }}>
                <Table
                  sx={
                    selectedOptions && selectedOptions.length > 0 ? undefined : { height: '100%' }
                  }
                >
                  <TableHead className={dialogStyles.tableHead}>
                    <TableRow>
                      <TableCell colSpan={2}>
                        <SimpleText id={selectedHeadingLabelId} />
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody className={tableStyles.tableBody}>
                    {paginatedSelectedOptions &&
                      paginatedSelectedOptions.length > 0 &&
                      paginatedSelectedOptions.map(option => (
                        <TableRow key={option.id}>
                          <TableCell padding="none" onClick={() => this.handleToggleOption(option)}>
                            {optionRenderer.apply(this, [option, OPTION_TYPE.RESULT])}
                          </TableCell>
                          <TableCell style={{ width: 48 }} align="right" padding="none">
                            <Tooltip title={intl.formatMessage({ id: removeActionTooltipLabelId })}>
                              <IconButton
                                className={styles.deleteIcon}
                                onClick={() => this.handleToggleOption(option)}
                              >
                                <DeleteIcon />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      ))}
                    {(!paginatedSelectedOptions || paginatedSelectedOptions.length === 0) && (
                      <TableRow>
                        <TableCell sx={{ border: 0 }}>
                          <Box
                            style={{ minHeight: '100%' }}
                            sx={{
                              display: 'flex',
                              flexDirection: 'column',
                              justifyContent: 'center',
                              maxHeight: '100%',
                            }}
                          >
                            <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                              <Typography variant="body1" component="div">
                                <SimpleText id={noSelectionLabelId} />
                              </Typography>
                            </Box>
                          </Box>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                  {selectedOptions &&
                    selectedOptions.length > PAGINATION_PER_PAGE[0] * 2 && (
                      <TableFooter>
                        <TableRow>{this.renderSelectedOptionsPagination()}</TableRow>
                      </TableFooter>
                    )}
                </Table>
              </Box>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button
            size="large"
            labelId={submitActionLabelId}
            variant="contained"
            color="primary"
            sx={{ ml: 1 }}
            onClick={() => onSubmit(selectedOptions)}
            disabled={
              intention === INTENTION_TYPE.ADD
                ? selectedOptions.length === 0
                : isEqual(selectedOptionIds, initialItemIds)
            }
          />
        </DialogActions>
      </Dialog>
    );
  }
}

export default injectIntl(MultiSelectDialog);
